// ─────────────────────────────────────────────────────────────────────────────
// Excel de Históricos — las 3 hojas del reporte global de campañas, con la forma visual de la
// plantilla del cliente `03 - Historicos.xlsx` (auditoría 2026-09-05): paleta azul acero propia
// (`excelColoresHistoricos`), banner de título + subtítulo, bloque de metadatos celeste, encabezado
// de tabla azul con «Ajustar texto», banding blanco/celeste y rejilla de bordes finos.
//
// EL TEXTO COMPLETO SALE DE i18n (`reports.history.*`), en los 7 idiomas: nombres de hoja, banner,
// metadatos, encabezados, la «Nota / definición» de cada KPI y el glosario de «Definiciones» —
// mismo criterio ya aplicado en Líderes y Demográfico. Un archivo que se comparte fuera de la app
// tiene que poder leerse sin saber español.
//
// Las etiquetas de dato se reusan de las claves que YA existen y que la pantalla también usa
// (`kpi*`, `col*`): así la columna del Excel y la de la tabla dicen lo mismo, y no hay dos
// traducciones del mismo concepto que puedan divergir.
//
// Vive fuera de `CampaignsGlobalExportButton.tsx` para que el componente se quede con la descarga y
// no con la especificación de estilos, igual que `leadersExcel.ts` frente a `LeadersTab.tsx`.
//
// Diferencias A PROPÓSITO frente a la plantilla de referencia:
//   1. Sin las hojas «Base Demo» y «Medición»: son el catálogo sintético del que la plantilla saca
//      sus fórmulas vivas, no contenido del reporte. Aquí los valores llegan ya calculados del
//      backend.
//   2. Sin los pies «Datos de ejemplo del entorno demo… no representan producción» ni «Nota de
//      medición demo»: describen el archivo de MUESTRA del cliente, no un reporte con datos reales
//      (mismo criterio ya aplicado en Líderes y en la Reportería de Moderación).
//   3. Los KPI y las definiciones de «Participantes Staff» / «Participantes Frontline» no están en
//      la plantilla — se agregaron en P18-ter a petición del cliente. Van al FINAL de las dos hojas
//      para que las seis filas que la plantilla sí trae salgan exactamente en su orden.
//   4. El banner va a 18 pt en las tres hojas. La plantilla alterna 16 y 18 pt entre pestañas, que
//      es una inconsistencia de un archivo hecho a mano, no una intención de diseño.
//   5. «Campañas Históricas» conserva el orden de columnas que el cliente confirmó para este
//      reporte (Nombre y Tipo primero, para identificar la fila), no el de la plantilla.
// ─────────────────────────────────────────────────────────────────────────────
import { excelColoresHistoricos } from '@/utils/excelTheme';
import { mergeDeFila } from '@/utils/exportExcelMultiSheet';
import type {
  ExcelCellColorHint, ExcelCellStyleHint, ExcelCellValue, ExcelColumnStyleHint, ExcelSheetSpec,
  ExcelSheetTheme,
} from '@/utils/exportExcelMultiSheet';

const FORMATO_ENTERO = '#,##0';
const FORMATO_PORCENTAJE = '0.0%';
const FORMATO_PROMEDIO = '0.0';

/** Fila 0-indexed del encabezado en «Definiciones» y «Campañas Históricas» (banner + 2 en blanco). */
const FILA_HEADER_DETALLE = 4;
/** Fila 0-indexed del encabezado de la tabla de KPIs en «Resumen Ejecutivo». */
const FILA_HEADER_RESUMEN = 6;
/** Filas 0-indexed del bloque «Filtro aplicado» / «Generado» del Resumen Ejecutivo. */
const FILAS_META = [3, 4];

/** Índice 0-based de «Estado» dentro del encabezado de «Campañas Históricas». */
const COLUMNA_ESTADO = 10;
const TOTAL_COLUMNAS_CAMPANAS = 12;
const TOTAL_COLUMNAS_DEFINICIONES = 6;
const TOTAL_COLUMNAS_RESUMEN = 3;

/**
 * Anchos de «Campañas Históricas». Los de la plantilla no se pueden copiar posición por posición
 * porque el orden de columnas es distinto (ver nota 5 del encabezado): se reasignaron al rótulo que
 * cada uno describe y se ampliaron los tres de texto libre (Nombre, Audiencia OU y Descripción),
 * que en la plantilla solo tenían que alojar los datos cortos del demo. Con el encabezado ajustado
 * a la celda, ninguna fila lleva alto explícito: Excel calcula el que cada idioma necesita.
 */
const ANCHOS_CAMPANAS = [26, 14, 26, 15, 14, 16, 18, 18.44, 16, 14, 13, 34];
/** Anchos del Resumen Ejecutivo, medidos de la plantilla (columnas A/B/C de «Resumen Ejecutivo»). */
const ANCHOS_RESUMEN = [46.66, 18.11, 49.55];
/**
 * Anchos de «Definiciones». La plantilla mide A, B, D, E y F pero deja «Cómo se mide» (C) sin ancho,
 * con lo que la prosa más larga de la hoja cae en la única columna estrecha; toma el mismo ancho
 * que su vecina «Qué es».
 */
const ANCHOS_DEFINICIONES = [33.33, 38.11, 38.11, 33.33, 38.11, 21];

/** Alto en puntos del banner de título, medido de la plantilla. */
const ALTO_TITULO = 23.4;

/** Clave i18n de la hoja en la que aparece un dato del glosario. */
const DONDE_RESUMEN = 'excel.whereSummary';
const DONDE_RESUMEN_Y_CAMPANAS = 'excel.whereSummaryCampaigns';
/** Staff y Frontline comparten una sola explicación: es el mismo dato partido en dos segmentos. */
const MEDICION_STAFF_FRONTLINE = 'excel.measuredStaffFrontline';
/** Alineación vertical compartida por todas las celdas de datos: ver `columnaDeTexto`. */
const CENTRADO_VERTICAL = 'middle' as const;

/**
 * Estado canónico de una campaña, ANTES de traducirlo. Espeja el retorno de `estadoVisible`
 * (`reportsShared.tsx`); se declara aquí para que este módulo no dependa de un componente de pantalla.
 */
export type EstadoCampanaVisible = 'activo' | 'expirado' | 'borrador';

/**
 * Color de TEXTO por estado en la columna «Estado» de «Campañas Históricas» — pedido explícito del
 * cliente 2026-09-02 (screenshot de su archivo de referencia). Se aplica por FILA/COLUMNA y no por
 * el texto ya traducido de la celda: `t()` devuelve un valor distinto en cada idioma.
 */
const ESTADO_COLOR_ARGB: Record<EstadoCampanaVisible, string> = {
  activo: 'FF15803D',
  expirado: 'FFB91C1C',
  borrador: 'FF374151',
};

/**
 * Paleta de las tres hojas. Se pasa como `styleHints.theme` (y no con `isDefaultThemeSkipped` +
 * `cellStyles`, como hace Líderes) porque esta plantilla SÍ usa banding zebra en sus tablas: dejar
 * que el tema lo pinte mantiene el costo del hint en O(columnas) en vez de O(filas × columnas).
 */
const TEMA_HISTORICOS: ExcelSheetTheme = {
  tituloFondo: excelColoresHistoricos.tituloFondo,
  tituloTexto: excelColoresHistoricos.tituloTexto,
  subtituloFondo: excelColoresHistoricos.subtituloFondo,
  subtituloTexto: excelColoresHistoricos.subtituloTexto,
  headerTablaFondo: excelColoresHistoricos.headerTablaFondo,
  headerTablaTexto: excelColoresHistoricos.headerTablaTexto,
  filaPar: excelColoresHistoricos.filaPar,
  filaImpar: excelColoresHistoricos.filaImpar,
  isHeaderWrapped: true,
  isSubtituloItalic: true,
};

/**
 * Resuelve un texto del namespace `recognition` bajo `reports.history`. Se recibe como parámetro y
 * no se importa el singleton de i18n: así el módulo no depende de él y una prueba puede pasar su
 * propio traductor.
 */
export type Traductor = (clave: string, opciones?: Record<string, unknown>) => string;

/** Una fila de «Campañas Históricas», con el texto ya resuelto en el idioma activo. */
export interface FilaCampanaHistorico {
  nombre: string;
  tipo: string;
  audienciaOu: string;
  audiencia: ExcelCellValue;
  participantes: ExcelCellValue;
  participantesStaff: ExcelCellValue;
  participantesFrontline: ExcelCellValue;
  duracion: string;
  reconocimientos: ExcelCellValue;
  expiracion: string;
  /** Estado canónico, el que decide el color de la celda. */
  estadoKey: EstadoCampanaVisible;
  /** Estado ya traducido, el que se escribe en la celda. */
  estado: string;
  descripcion: string;
}

/**
 * KPIs del Resumen Ejecutivo. Los conteos viajan como NÚMERO (o como el guion de «sin dato» cuando
 * el agregado está suprimido por anonimato), nunca como texto ya formateado: un `"77.9%"` string se
 * alineaba a la izquierda mientras los conteos numéricos se iban a la derecha, y la columna «Valor»
 * salía con los datos en dos alineaciones distintas. Así, además, el número sigue siendo sumable y
 * ordenable en Excel.
 */
export interface KpisHistorico {
  totalCampanas: ExcelCellValue;
  totalReconocimientos: ExcelCellValue;
  audienciaTotal: ExcelCellValue;
  participantes: ExcelCellValue;
  participantesStaff: ExcelCellValue;
  participantesFrontline: ExcelCellValue;
  /** Fracción 0-1, no porcentaje ya multiplicado: el formato `0.0%` de Excel hace la conversión. */
  porcentajeParticipacion: ExcelCellValue;
  promedioPorParticipante: ExcelCellValue;
}

/** Banner de una hoja: título azul oscuro sangrado y subtítulo celeste en cursiva, ambos a lo ancho. */
function estilosDeBanner(anchoEnColumnas: number): {
  cellStyles: ExcelCellStyleHint[];
  merges: string[];
} {
  return {
    cellStyles: [
      { row: 0, column: 0, fontSize: 18, horizontal: 'left', vertical: 'middle', indent: 1 },
      { row: 1, column: 0, horizontal: 'left', vertical: 'middle', indent: 1, hasWrapText: true },
    ],
    merges: [mergeDeFila(0, 0, anchoEnColumnas - 1), mergeDeFila(1, 0, anchoEnColumnas - 1)],
  };
}

/** Columna de prosa: alineada arriba a la izquierda y ajustada a la celda para no desbordar. */
function columnaDeProsa(columna: number): ExcelColumnStyleHint {
  return {
    column: columna,
    fontColorArgb: excelColoresHistoricos.textoCuerpo,
    horizontal: 'left',
    vertical: 'top',
    hasWrapText: true,
  };
}

/** Columna de texto de una tabla de datos: a la izquierda, centrada en vertical y ajustada. */
function columnaDeTexto(columna: number): ExcelColumnStyleHint {
  return { ...columnaDeProsa(columna), vertical: CENTRADO_VERTICAL };
}

/** Columna numérica: alineada a la derecha, con formato nativo de Excel. */
function columnaNumerica(columna: number, numFmt: string): ExcelColumnStyleHint {
  return {
    column: columna,
    fontColorArgb: excelColoresHistoricos.textoCuerpo,
    horizontal: 'right',
    vertical: CENTRADO_VERTICAL,
    numFmt,
  };
}

/**
 * Hoja «Resumen Ejecutivo». El orden de los KPI es el de la plantilla y no debe reordenarse: total
 * de campañas, reconocimientos, audiencia, participantes, tasa de participación y promedio por
 * participante. Staff y Frontline cierran la tabla — ver nota 3 del encabezado del archivo.
 */
function buildResumenSheet(
  traducir: Traductor, kpis: KpisHistorico, filtro: string, generado: string,
): ExcelSheetSpec {
  const banner = estilosDeBanner(TOTAL_COLUMNAS_RESUMEN);

  const kpiRows: Array<{ label: string; valor: ExcelCellValue; nota: string; formato: string }> = [
    {
      label: traducir('kpiTotalCampaigns'),
      valor: kpis.totalCampanas,
      nota: traducir('excel.measuredCampaigns'),
      formato: FORMATO_ENTERO,
    },
    {
      label: traducir('kpiTotalRecognitions'),
      valor: kpis.totalReconocimientos,
      nota: traducir('excel.measuredRecognitions'),
      formato: FORMATO_ENTERO,
    },
    {
      label: traducir('kpiAudienceTotal'),
      valor: kpis.audienciaTotal,
      nota: traducir('excel.measuredAudience'),
      formato: FORMATO_ENTERO,
    },
    {
      label: traducir('kpiParticipantsTotal'),
      valor: kpis.participantes,
      nota: traducir('excel.measuredParticipants'),
      formato: FORMATO_ENTERO,
    },
    {
      label: traducir('kpiParticipationRate'),
      valor: kpis.porcentajeParticipacion,
      nota: traducir('excel.measuredRate'),
      formato: FORMATO_PORCENTAJE,
    },
    {
      label: traducir('kpiAvgPerParticipant'),
      valor: kpis.promedioPorParticipante,
      nota: traducir('excel.measuredAvg'),
      formato: FORMATO_PROMEDIO,
    },
    {
      label: traducir('kpiParticipantsStaff'),
      valor: kpis.participantesStaff,
      nota: traducir(MEDICION_STAFF_FRONTLINE),
      formato: FORMATO_ENTERO,
    },
    {
      label: traducir('kpiParticipantsFrontline'),
      valor: kpis.participantesFrontline,
      nota: traducir(MEDICION_STAFF_FRONTLINE),
      formato: FORMATO_ENTERO,
    },
  ];

  const rows: ExcelCellValue[][] = [
    [traducir('excel.summaryTitle')],
    [traducir('excel.summarySubtitle')],
    [],
    [traducir('excel.metaFilter'), filtro],
    [traducir('excel.metaGenerated'), generado],
    [],
    [traducir('excel.colKpi'), traducir('excel.colValue'), traducir('excel.colNote')],
    ...kpiRows.map((kpi): ExcelCellValue[] => [kpi.label, kpi.valor, kpi.nota]),
  ];

  const primeraFilaKpi = FILA_HEADER_RESUMEN + 1;
  const ultimaFilaKpi = primeraFilaKpi + kpiRows.length - 1;

  return {
    name: traducir('excel.sheetSummary'),
    rows,
    styleHints: {
      theme: TEMA_HISTORICOS,
      headerRows: [FILA_HEADER_RESUMEN],
      columnWidths: ANCHOS_RESUMEN,
      rowHeights: [{ row: 0, height: ALTO_TITULO }],
      merges: [
        ...banner.merges,
        // El valor del metadato ocupa las dos columnas restantes: un filtro con varias OUs no cabe
        // en el ancho de «Valor».
        ...FILAS_META.map((fila) => mergeDeFila(fila, 1, TOTAL_COLUMNAS_RESUMEN - 1)),
      ],
      dataColumnStyles: {
        startRow: primeraFilaKpi,
        endRow: ultimaFilaKpi,
        columns: [
          {
            column: 0,
            fontColorArgb: excelColoresHistoricos.textoCuerpo,
            horizontal: 'left',
            vertical: CENTRADO_VERTICAL,
            hasWrapText: true,
          },
          {
            column: 1,
            fontColorArgb: excelColoresHistoricos.valorDestacado,
            isBold: true,
            horizontal: 'right',
            vertical: CENTRADO_VERTICAL,
          },
          { ...columnaDeProsa(2), vertical: CENTRADO_VERTICAL },
        ],
      },
      cellStyles: [
        ...banner.cellStyles,
        // Bloque de metadatos: etiqueta y valor comparten el celeste de la plantilla; solo la
        // etiqueta va en negrita y en el índigo de la marca.
        ...FILAS_META.flatMap((fila): ExcelCellStyleHint[] => [
          {
            row: fila,
            column: 0,
            fillArgb: excelColoresHistoricos.metaFondo,
            fontColorArgb: excelColoresHistoricos.metaEtiqueta,
            isBold: true,
            vertical: CENTRADO_VERTICAL,
          },
          {
            row: fila,
            column: 1,
            fillArgb: excelColoresHistoricos.metaFondo,
            fontColorArgb: excelColoresHistoricos.metaValor,
            hasWrapText: true,
            vertical: CENTRADO_VERTICAL,
          },
        ]),
        // Un formato numérico por FILA: la columna «Valor» mezcla conteos, un porcentaje y un
        // promedio, así que no puede fijarse a nivel de columna.
        ...kpiRows.map((kpi, indice): ExcelCellStyleHint => (
          { row: primeraFilaKpi + indice, column: 1, numFmt: kpi.formato }
        )),
      ],
      borderedRanges: [
        {
          startRow: FILAS_META[0],
          endRow: FILAS_META[FILAS_META.length - 1],
          startColumn: 0,
          endColumn: TOTAL_COLUMNAS_RESUMEN - 1,
          colorArgb: excelColoresHistoricos.bordeTabla,
        },
        {
          startRow: FILA_HEADER_RESUMEN,
          endRow: ultimaFilaKpi,
          startColumn: 0,
          endColumn: TOTAL_COLUMNAS_RESUMEN - 1,
          colorArgb: excelColoresHistoricos.bordeTabla,
        },
      ],
    },
  };
}

/**
 * Hoja «Definiciones» — las seis columnas del glosario de la plantilla (Dato · Qué es · Cómo se
 * mide · Cómo se calcula · Se compara contra · Dónde aparece) y sus seis filas en el mismo orden,
 * más la de Staff/Frontline que el reporte sí exporta y la plantilla no explicaba.
 */
function buildDefinicionesSheet(traducir: Traductor): ExcelSheetSpec {
  const banner = estilosDeBanner(TOTAL_COLUMNAS_DEFINICIONES);

  /** `[clave del rótulo, «Qué es», «Cómo se mide», «Cómo se calcula», «Se compara contra», «Dónde»]`. */
  const definiciones: Array<[string, string, string, string, string, string]> = [
    ['kpiTotalCampaigns', 'excel.whatCampaigns', 'excel.measuredCampaigns', 'excel.calcCampaigns', 'excel.vsCampaigns', DONDE_RESUMEN],
    ['kpiTotalRecognitions', 'excel.whatRecognitions', 'excel.measuredRecognitions', 'excel.calcRecognitions', 'excel.vsRecognitions', DONDE_RESUMEN],
    ['kpiAudienceTotal', 'excel.whatAudience', 'excel.measuredAudience', 'excel.calcAudience', 'excel.vsAudience', DONDE_RESUMEN],
    ['kpiParticipantsTotal', 'excel.whatParticipants', 'excel.measuredParticipants', 'excel.calcParticipants', 'excel.vsParticipants', DONDE_RESUMEN],
    ['kpiParticipationRate', 'excel.whatRate', 'excel.measuredRate', 'excel.calcRate', 'excel.vsRate', DONDE_RESUMEN],
    ['kpiAvgPerParticipant', 'excel.whatAvg', 'excel.measuredAvg', 'excel.calcAvg', 'excel.vsAvg', DONDE_RESUMEN],
    ['excel.datumStaffFrontline', 'excel.whatStaffFrontline', MEDICION_STAFF_FRONTLINE, 'excel.calcStaffFrontline', 'excel.vsStaffFrontline', DONDE_RESUMEN_Y_CAMPANAS],
  ];

  const rows: ExcelCellValue[][] = [
    [traducir('excel.definitionsTitle')],
    [traducir('excel.definitionsSubtitle')],
    [],
    [],
    [
      traducir('excel.colDatum'), traducir('excel.colWhatIs'), traducir('excel.colHowMeasured'),
      traducir('excel.colHowCalculated'), traducir('excel.colComparedTo'),
      traducir('excel.colWhereShown'),
    ],
    ...definiciones.map((claves): ExcelCellValue[] => claves.map((clave) => traducir(clave))),
  ];

  const primeraFilaDato = FILA_HEADER_DETALLE + 1;
  const ultimaFilaDato = primeraFilaDato + definiciones.length - 1;

  return {
    name: traducir('excel.sheetDefinitions'),
    rows,
    styleHints: {
      theme: TEMA_HISTORICOS,
      headerRows: [FILA_HEADER_DETALLE],
      columnWidths: ANCHOS_DEFINICIONES,
      rowHeights: [{ row: 0, height: ALTO_TITULO }],
      merges: banner.merges,
      dataColumnStyles: {
        startRow: primeraFilaDato,
        endRow: ultimaFilaDato,
        columns: [
          { ...columnaDeProsa(0), isBold: true },
          ...Array.from(
            { length: TOTAL_COLUMNAS_DEFINICIONES - 1 },
            (_, indice) => columnaDeProsa(indice + 1),
          ),
        ],
      },
      cellStyles: banner.cellStyles,
      borderedRanges: [{
        startRow: FILA_HEADER_DETALLE,
        endRow: ultimaFilaDato,
        startColumn: 0,
        endColumn: TOTAL_COLUMNAS_DEFINICIONES - 1,
        colorArgb: excelColoresHistoricos.bordeTabla,
      }],
    },
  };
}

/**
 * Hoja «Campañas Históricas» — una fila por campaña. Los conteos viajan como número con formato
 * `#,##0` para que toda la mitad numérica de la tabla quede alineada a la derecha y siga siendo
 * ordenable en Excel; las columnas de texto van a la izquierda y «Estado» centrada, con el color
 * que le corresponde a su valor.
 */
function buildCampanasSheet(
  traducir: Traductor, campanas: readonly FilaCampanaHistorico[],
): ExcelSheetSpec {
  const banner = estilosDeBanner(TOTAL_COLUMNAS_CAMPANAS);

  const rows: ExcelCellValue[][] = [
    [traducir('excel.sheetCampaigns')],
    [traducir('excel.campaignsSubtitle')],
    [],
    [],
    [
      traducir('colName'), traducir('colType'), traducir('excel.colAudienceOu'),
      traducir('kpiAudienceTotal'), traducir('colParticipants'), traducir('colParticipantsStaff'),
      traducir('colParticipantsFrontline'), traducir('colDuration'), traducir('colRecognitions'),
      traducir('colExpiration'), traducir('colStatus'), traducir('colDescription'),
    ],
    ...campanas.map((campana): ExcelCellValue[] => [
      campana.nombre, campana.tipo, campana.audienciaOu, campana.audiencia, campana.participantes,
      campana.participantesStaff, campana.participantesFrontline, campana.duracion,
      campana.reconocimientos, campana.expiracion, campana.estado, campana.descripcion,
    ]),
  ];

  const primeraFilaDato = FILA_HEADER_DETALLE + 1;
  const ultimaFilaDato = primeraFilaDato + campanas.length - 1;

  const coloresEstado: ExcelCellColorHint[] = campanas.map((campana, indice) => ({
    row: primeraFilaDato + indice,
    column: COLUMNA_ESTADO,
    colorArgb: ESTADO_COLOR_ARGB[campana.estadoKey],
    bold: true,
  }));

  return {
    name: traducir('excel.sheetCampaigns'),
    rows,
    styleHints: {
      theme: TEMA_HISTORICOS,
      headerRows: [FILA_HEADER_DETALLE],
      columnWidths: ANCHOS_CAMPANAS,
      rowHeights: [{ row: 0, height: ALTO_TITULO }],
      merges: banner.merges,
      dataColumnStyles: {
        startRow: primeraFilaDato,
        endRow: ultimaFilaDato,
        // Todas las columnas centradas en vertical, también las de texto: con «Descripción»
        // ajustada a la celda, una fila puede ocupar dos o tres renglones y los números alineados
        // arriba dejarían la fila leyéndose en dos alturas distintas.
        columns: [
          columnaDeTexto(0),
          columnaDeTexto(1),
          columnaDeTexto(2),
          columnaNumerica(3, FORMATO_ENTERO),
          columnaNumerica(4, FORMATO_ENTERO),
          columnaNumerica(5, FORMATO_ENTERO),
          columnaNumerica(6, FORMATO_ENTERO),
          { ...columnaDeTexto(7), horizontal: 'center' },
          columnaNumerica(8, FORMATO_ENTERO),
          { ...columnaDeTexto(9), horizontal: 'center' },
          { ...columnaDeTexto(COLUMNA_ESTADO), horizontal: 'center' },
          columnaDeTexto(11),
        ],
      },
      // `cellTextColors` se aplica DESPUÉS de `dataColumnStyles`, así que el color por estado gana
      // sobre el color de cuerpo de la columna.
      cellTextColors: coloresEstado,
      cellStyles: banner.cellStyles,
      borderedRanges: [{
        startRow: FILA_HEADER_DETALLE,
        endRow: ultimaFilaDato,
        startColumn: 0,
        endColumn: TOTAL_COLUMNAS_CAMPANAS - 1,
        colorArgb: excelColoresHistoricos.bordeTabla,
      }],
    },
  };
}

/**
 * Las 3 hojas del Excel de Históricos, en el orden del libro de referencia: Resumen Ejecutivo,
 * Definiciones y el detalle por campaña.
 */
export function buildHistoricosExcelSheets(parametros: Readonly<{
  /** Resuelve una clave RELATIVA a `reports.history` del namespace `recognition`. */
  traducir: Traductor;
  kpis: KpisHistorico;
  campanas: readonly FilaCampanaHistorico[];
  /** Alcance del reporte, ya resuelto en el idioma activo. */
  filtro: string;
  /** Fecha y hora de generación, ya formateada en el idioma activo por el caller. */
  generado: string;
}>): ExcelSheetSpec[] {
  const { traducir } = parametros;
  return [
    buildResumenSheet(traducir, parametros.kpis, parametros.filtro, parametros.generado),
    buildDefinicionesSheet(traducir),
    buildCampanasSheet(traducir, parametros.campanas),
  ];
}
